import { useEffect, useState } from 'react';
import styled from 'styled-components';

export const LOADING_BAR_ANIMATION_DURATION_MS = 500;

interface LoadingBarProps {
  useTransition: boolean;
}

const LoadingBar = styled('circle')<LoadingBarProps>`
  ${(props) =>
    props.useTransition
      ? `transition: stroke-dashoffset ${LOADING_BAR_ANIMATION_DURATION_MS}ms ease-in`
      : ''};
`;

const RotatedGroup = styled.div`
  transform: rotate(90deg);
`;

interface SVGProps {
  width: number;
  strokeWidth: number;
  color?: string;
}

interface LoadingCircleProps extends SVGProps {
  percent: number;
}

/**
 * This component is the circle outline that indicates what sides are captured.
 * It can be 0%, 50%, and 100% full.
 */
export default function LoadingCircle({ width, strokeWidth, percent, color }: LoadingCircleProps) {
  const [useTransition, setUseTransition] = useState(false);

  // NB:
  // If we always have transition enabled for the dashoffset, we see
  // weird transitions when the properties other than percentage change.
  // If the the width changes, this will cause the dashoffset to change
  // triggering a transition which we dont want, especially when the percentage
  // is 0
  useEffect(() => {
    setUseTransition(true);

    const timeout = setTimeout(() => {
      setUseTransition(false);
    }, 500);

    return () => clearTimeout(timeout);
  }, [percent]);

  const radius = width / 2 - strokeWidth * 2;
  const circumference = Math.PI * radius * 2;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <RotatedGroup role='img' aria-label='loading circle'>
      <svg width={width} height={width}>
        <LoadingBar
          cx={width / 2}
          cy={width / 2}
          r={radius}
          fill='none'
          stroke={color}
          strokeWidth={`${strokeWidth}px`}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
          useTransition={useTransition}
        />
      </svg>
    </RotatedGroup>
  );
}
